import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSpaces } from '../context/SpaceContext';
import { listUsers } from '../services/userService';
import { getProjects } from '../services/projectService';
import {
  addBusinessMember,
  addBusinessTeam,
  approveBusinessJoin,
  createBusiness,
  getBusinesses,
  getBusiness,
  linkBusinessProject,
  linkBusinessSpace,
  rejectBusinessJoin,
  removeBusinessMember,
  removeBusinessTeam,
  requestBusinessJoin,
  unlinkBusinessProject,
  unlinkBusinessSpace,
  updateBusiness,
  updateBusinessMember,
} from '../services/businessService';
import './BusinessesPage.css';

const ROLE_OPTIONS = ['owner', 'admin', 'member', 'viewer'];

const initialBusinessForm = {
  name: '',
  description: '',
  website: '',
  industry: '',
  joinPolicy: 'request',
};

export default function BusinessesPage() {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth() || {};
  const { spaces = [] } = useSpaces() || {};

  const [businesses, setBusinesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewBusiness, setShowNewBusiness] = useState(false);
  const [newBusiness, setNewBusiness] = useState(initialBusinessForm);
  const [savingBusiness, setSavingBusiness] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(initialBusinessForm);
  const [joinMessage, setJoinMessage] = useState('');
  const [memberUserId, setMemberUserId] = useState('');
  const [memberRole, setMemberRole] = useState('member');
  const [memberTeamId, setMemberTeamId] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamDescription, setTeamDescription] = useState('');
  const [linkSpaceId, setLinkSpaceId] = useState('');
  const [linkProjectId, setLinkProjectId] = useState('');
  const [updatingMemberId, setUpdatingMemberId] = useState('');
  const currentUserId = user?._id;

  const refreshBusiness = async (id) => {
    const res = await getBusiness(id);
    const nextBusiness = res.data.business || null;
    const spacesForBusiness = Array.isArray(res.data.spaces) ? res.data.spaces : [];
    const projectsForBusiness = Array.isArray(res.data.projects) ? res.data.projects : [];
    const hydrated = nextBusiness ? { ...nextBusiness, spaces: spacesForBusiness, projects: projectsForBusiness } : null;
    setSelectedBusiness(hydrated);
    return hydrated;
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all([getBusinesses(), listUsers(), getProjects()])
      .then(([businessRes, usersRes, projectsRes]) => {
        if (!active) return;
        const list = businessRes.data.businesses || [];
        setBusinesses(list);
        setAllUsers(usersRes.data.users || []);
        setProjects(projectsRes.data.projects || []);
        if (businessId) {
          const found = list.find((business) => business._id === businessId);
          if (found) {
            setSelectedBusiness(found);
            refreshBusiness(found._id).catch((err) => console.error('Failed to load business', err));
          }
        }
      })
      .catch((err) => console.error('Failed to load businesses:', err))
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [businessId]);

  const visibleUsers = useMemo(() => {
    const members = selectedBusiness?.members || [];
    const memberIds = new Set(members.map((member) => String(member.user?._id || member.user)));
    return allUsers.filter((user) => !memberIds.has(String(user._id)));
  }, [allUsers, selectedBusiness]);

  const selectedTeams = selectedBusiness?.teams || [];
  const selectedRequests = selectedBusiness?.joinRequests || [];

  useEffect(() => {
    if (selectedBusiness) {
      setEditData({
        name: selectedBusiness.name || '',
        description: selectedBusiness.description || '',
        website: selectedBusiness.website || '',
        industry: selectedBusiness.industry || '',
        joinPolicy: selectedBusiness.joinPolicy || 'request',
      });
    }
  }, [selectedBusiness]);

  const openBusiness = async (business) => {
    navigate(`/businesses/${business._id}`, { replace: true });
    try {
      await refreshBusiness(business._id);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to open business details';
      console.error('Failed to open business:', err);
      alert(message);
      setSelectedBusiness(business);
    }
  };

  const handleCreateBusiness = async (e) => {
    e.preventDefault();
    if (!newBusiness.name.trim()) return;
    setSavingBusiness(true);
    try {
      const res = await createBusiness(newBusiness);
      const created = res.data.business;
      setBusinesses((prev) => [created, ...prev]);
      setNewBusiness(initialBusinessForm);
      setShowNewBusiness(false);
      await openBusiness(created);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to create business');
    } finally {
      setSavingBusiness(false);
    }
  };

  const handleSaveBusiness = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      const res = await updateBusiness(selectedBusiness._id, editData);
      const updated = res.data.business;
      setBusinesses((prev) => prev.map((business) => (business._id === updated._id ? updated : business)));
      await refreshBusiness(updated._id);
      setEditing(false);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update business');
    }
  };

  const handleRequestJoin = async (e) => {
    e.preventDefault();
    if (!selectedBusiness) return;
    try {
      const res = await requestBusinessJoin(selectedBusiness._id, { message: joinMessage });
      await refreshBusiness(res.data.business._id);
      setJoinMessage('');
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to request join');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !memberUserId) return;
    try {
      await addBusinessMember(selectedBusiness._id, memberUserId, memberRole, memberTeamId || null);
      setMemberUserId('');
      setMemberRole('member');
      setMemberTeamId('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add member');
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedBusiness) return;
    try {
      await removeBusinessMember(selectedBusiness._id, userId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove member');
    }
  };

  const handleUpdateMemberRole = async (targetUserId, role) => {
    if (!selectedBusiness || !targetUserId || !role) return;
    setUpdatingMemberId(String(targetUserId));
    try {
      await updateBusinessMember(selectedBusiness._id, targetUserId, { role });
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to update member role');
    } finally {
      setUpdatingMemberId('');
    }
  };

  const handleAddTeam = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !teamName.trim()) return;
    try {
      await addBusinessTeam(selectedBusiness._id, { name: teamName, description: teamDescription });
      setTeamName('');
      setTeamDescription('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to add team');
    }
  };

  const handleRemoveTeam = async (teamIndex) => {
    if (!selectedBusiness) return;
    try {
      await removeBusinessTeam(selectedBusiness._id, teamIndex);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to remove team');
    }
  };

  const handleApproveRequest = async (requesterId) => {
    if (!selectedBusiness) return;
    try {
      await approveBusinessJoin(selectedBusiness._id, requesterId, {});
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to approve request');
    }
  };

  const handleRejectRequest = async (requesterId) => {
    if (!selectedBusiness) return;
    try {
      await rejectBusinessJoin(selectedBusiness._id, requesterId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to reject request');
    }
  };

  const handleLinkSpace = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !linkSpaceId) return;
    try {
      await linkBusinessSpace(selectedBusiness._id, linkSpaceId);
      setLinkSpaceId('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to link space');
    }
  };

  const handleUnlinkSpace = async (spaceId) => {
    if (!selectedBusiness) return;
    try {
      await unlinkBusinessSpace(selectedBusiness._id, spaceId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unlink space');
    }
  };

  const handleLinkProject = async (e) => {
    e.preventDefault();
    if (!selectedBusiness || !linkProjectId) return;
    try {
      await linkBusinessProject(selectedBusiness._id, linkProjectId);
      setLinkProjectId('');
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to link project');
    }
  };

  const handleUnlinkProject = async (projectId) => {
    if (!selectedBusiness) return;
    try {
      await unlinkBusinessProject(selectedBusiness._id, projectId);
      await refreshBusiness(selectedBusiness._id);
    } catch (err) {
      alert(err?.response?.data?.message || 'Failed to unlink project');
    }
  };

  const isMember = Boolean(selectedBusiness?.members?.some((member) => String(member.user?._id || member.user) === String(currentUserId)));
  const ownerId = selectedBusiness?.owner?._id || selectedBusiness?.owner;
  const ownerIds = (() => {
    const ids = new Set();
    if (ownerId) ids.add(String(ownerId));
    for (const member of selectedBusiness?.members || []) {
      if (member.role === 'owner') {
        ids.add(String(member.user?._id || member.user));
      }
    }
    return ids;
  })();
  const ownerCount = ownerIds.size;
  const currentBusinessRole = String(ownerId) === String(currentUserId)
    ? 'owner'
    : (selectedBusiness?.members || []).find((member) => String(member.user?._id || member.user) === String(currentUserId))?.role || null;
  const canManageMembers = currentBusinessRole === 'owner' || currentBusinessRole === 'admin';
  const roleOptionsForEditor = currentBusinessRole === 'owner'
    ? ROLE_OPTIONS
    : ROLE_OPTIONS.filter((role) => role !== 'owner');

  return (
    <div className="businesses-page">
      <aside className="businesses-sidebar">
        <div className="businesses-sidebar-header">
          <h2 className="businesses-sidebar-title">Businesses</h2>
          <button className="btn btn-primary btn-sm" onClick={() => setShowNewBusiness((v) => !v)}>
            + New
          </button>
        </div>

        {showNewBusiness && (
          <form className="business-new-form" onSubmit={handleCreateBusiness}>
            <input className="form-input" autoFocus placeholder="Business name *" value={newBusiness.name} onChange={(e) => setNewBusiness((prev) => ({ ...prev, name: e.target.value }))} />
            <input className="form-input" placeholder="Description" value={newBusiness.description} onChange={(e) => setNewBusiness((prev) => ({ ...prev, description: e.target.value }))} />
            <input className="form-input" placeholder="Website" value={newBusiness.website} onChange={(e) => setNewBusiness((prev) => ({ ...prev, website: e.target.value }))} />
            <input className="form-input" placeholder="Industry" value={newBusiness.industry} onChange={(e) => setNewBusiness((prev) => ({ ...prev, industry: e.target.value }))} />
            <select className="form-select" value={newBusiness.joinPolicy} onChange={(e) => setNewBusiness((prev) => ({ ...prev, joinPolicy: e.target.value }))}>
              <option value="request">Join requests</option>
              <option value="open">Open</option>
              <option value="invite">Invite only</option>
            </select>
            <button className="btn btn-primary btn-sm" disabled={savingBusiness} type="submit">
              {savingBusiness ? 'Creating...' : 'Create Business'}
            </button>
          </form>
        )}

        <ul className="businesses-list">
          {businesses.map((business) => (
            <li key={business._id}>
              <button className={`business-list-item ${selectedBusiness?._id === business._id ? 'active' : ''}`} onClick={() => openBusiness(business)}>
                <span className="business-avatar">{business.name?.[0]?.toUpperCase()}</span>
                <span className="business-list-info">
                  <span className="business-list-name">{business.name}</span>
                  <span className="business-list-meta">{business.industry || business.joinPolicy}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </aside>

      <main className="business-detail">
        {loading ? (
          <div className="business-detail-empty">
            <div>
              <h1>Loading businesses</h1>
              <p>Fetching businesses, users, and projects.</p>
            </div>
          </div>
        ) : !selectedBusiness ? (
          <div className="business-detail-empty">
            <div>
              <h1>Businesses</h1>
              <p>Create a business to organize users, teams, and access across spaces and projects.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="business-detail-header">
              <div className="business-detail-title-block">
                <h1>{selectedBusiness.name}</h1>
                <p>{selectedBusiness.description || 'No description yet.'}</p>
              </div>
              <div className="business-detail-actions">
                <button className="btn btn-ghost btn-sm" onClick={() => setEditing((v) => !v)}>
                  {editing ? 'Close Edit' : 'Edit'}
                </button>
              </div>
            </div>

            {editing ? (
              <form className="business-edit-form" onSubmit={handleSaveBusiness}>
                <input className="form-input" value={editData.name} onChange={(e) => setEditData((prev) => ({ ...prev, name: e.target.value }))} />
                <textarea className="form-textarea" rows={3} value={editData.description} onChange={(e) => setEditData((prev) => ({ ...prev, description: e.target.value }))} />
                <input className="form-input" value={editData.website} onChange={(e) => setEditData((prev) => ({ ...prev, website: e.target.value }))} placeholder="Website" />
                <input className="form-input" value={editData.industry} onChange={(e) => setEditData((prev) => ({ ...prev, industry: e.target.value }))} placeholder="Industry" />
                <select className="form-select" value={editData.joinPolicy} onChange={(e) => setEditData((prev) => ({ ...prev, joinPolicy: e.target.value }))}>
                  <option value="request">Join requests</option>
                  <option value="open">Open</option>
                  <option value="invite">Invite only</option>
                </select>
                <div className="form-actions">
                  <button className="btn btn-primary" type="submit">Save Business</button>
                </div>
              </form>
            ) : (
              <div className="business-summary-grid">
                <div className="business-summary-item"><span>Website</span><strong>{selectedBusiness.website || 'Not set'}</strong></div>
                <div className="business-summary-item"><span>Industry</span><strong>{selectedBusiness.industry || 'Not set'}</strong></div>
                <div className="business-summary-item"><span>Join Policy</span><strong>{selectedBusiness.joinPolicy}</strong></div>
                <div className="business-summary-item"><span>Owner</span><strong>{selectedBusiness.owner?.name || 'Unknown'}</strong></div>
              </div>
            )}

            {!isMember && selectedBusiness.joinPolicy !== 'invite' && (
              <form className="business-request-form" onSubmit={handleRequestJoin}>
                <h3>Request to join</h3>
                <textarea className="form-textarea" rows={3} placeholder="Tell the business why you should join" value={joinMessage} onChange={(e) => setJoinMessage(e.target.value)} />
                <button className="btn btn-primary btn-sm" type="submit">Request Join</button>
              </form>
            )}

            <section className="business-section">
              <h3>Members</h3>
              {canManageMembers && (
                <form className="business-inline-form" onSubmit={handleAddMember}>
                  <select className="form-select" value={memberUserId} onChange={(e) => setMemberUserId(e.target.value)}>
                    <option value="">Select user</option>
                    {visibleUsers.map((user) => <option key={user._id} value={user._id}>{user.name} — {user.email}</option>)}
                  </select>
                  <select className="form-select form-select-sm" value={memberRole} onChange={(e) => setMemberRole(e.target.value)}>
                    {roleOptionsForEditor.map((role) => <option key={role} value={role}>{role}</option>)}
                  </select>
                  <select className="form-select form-select-sm" value={memberTeamId} onChange={(e) => setMemberTeamId(e.target.value)}>
                    <option value="">No team</option>
                    {selectedTeams.map((team, index) => <option key={`${team.name}-${index}`} value={team._id || index}>{team.name}</option>)}
                  </select>
                  <button className="btn btn-primary btn-sm" type="submit">Add</button>
                </form>
              )}
              <ul className="business-member-list">
                {(selectedBusiness.members || []).map((member) => (
                  <li key={member.user?._id || member.user} className="business-member-item">
                    <div className="business-member-info">
                      <strong>{member.user?.name || 'Member'}</strong>
                      <span>{member.user?.email || ''}</span>
                    </div>
                    {(() => {
                      const targetId = member.user?._id || member.user;
                      const targetRole = member.role;
                      const targetIsOnlyOwner = targetRole === 'owner' && ownerCount <= 1;
                      const adminBlockedByOwnerTarget = currentBusinessRole === 'admin' && targetRole === 'owner';
                      const canEditRole = canManageMembers && !targetIsOnlyOwner && !adminBlockedByOwnerTarget;
                      const canRemove = canManageMembers && !targetIsOnlyOwner && !adminBlockedByOwnerTarget;

                      return (
                        <>
                          {canEditRole ? (
                            <select
                              className="form-select form-select-sm business-member-role-select"
                              value={targetRole}
                              onChange={(e) => handleUpdateMemberRole(targetId, e.target.value)}
                              disabled={updatingMemberId === String(targetId)}
                            >
                              {roleOptionsForEditor.map((role) => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="business-member-role">{targetRole}</span>
                          )}

                          {canRemove && (
                            <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveMember(targetId)}>Remove</button>
                          )}
                        </>
                      );
                    })()}
                  </li>
                ))}
              </ul>
            </section>

            <section className="business-section">
              <h3>Teams</h3>
              <form className="business-inline-form" onSubmit={handleAddTeam}>
                <input className="form-input" placeholder="Team name" value={teamName} onChange={(e) => setTeamName(e.target.value)} />
                <input className="form-input" placeholder="Description" value={teamDescription} onChange={(e) => setTeamDescription(e.target.value)} />
                <button className="btn btn-primary btn-sm" type="submit">Add Team</button>
              </form>
              <div className="business-team-list">
                {selectedTeams.map((team, index) => (
                  <div key={`${team.name}-${index}`} className="business-team-card">
                    <div className="business-team-header">
                      <div>
                        <strong>{team.name}</strong>
                        <p>{team.description || 'No description'}</p>
                      </div>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleRemoveTeam(index)}>Remove</button>
                    </div>
                    <div className="business-team-members">
                      {(team.memberIds || []).map((person) => <span key={person._id || person}>{person.name || person.email || 'Member'}</span>)}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="business-section">
              <h3>Join Requests</h3>
              <div className="business-request-list">
                {(selectedRequests || []).filter((request) => request.status === 'pending').map((request) => (
                  <div key={`${request.requester?._id || request.requester}-${request.requestedAt}`} className="business-request-item">
                    <div>
                      <strong>{request.requester?.name || 'User'}</strong>
                      <p>{request.message || 'No message'}</p>
                    </div>
                    <div className="business-request-actions">
                      <button className="btn btn-primary btn-sm" onClick={() => handleApproveRequest(request.requester?._id || request.requester)}>Approve</button>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleRejectRequest(request.requester?._id || request.requester)}>Reject</button>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="business-section">
              <h3>Linked Spaces</h3>
              <form className="business-inline-form" onSubmit={handleLinkSpace}>
                <select className="form-select" value={linkSpaceId} onChange={(e) => setLinkSpaceId(e.target.value)}>
                  <option value="">Select space</option>
                  {spaces.map((space) => <option key={space._id} value={space._id}>{space.name}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" type="submit">Link Space</button>
              </form>
              <div className="business-link-list">
                {(selectedBusiness.spaces || []).map((space) => (
                  <div key={space._id} className="business-link-item">
                    <span>{space.name}</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleUnlinkSpace(space._id)}>Unlink</button>
                  </div>
                ))}
              </div>
            </section>

            <section className="business-section">
              <h3>Linked Projects</h3>
              <form className="business-inline-form" onSubmit={handleLinkProject}>
                <select className="form-select" value={linkProjectId} onChange={(e) => setLinkProjectId(e.target.value)}>
                  <option value="">Select project</option>
                  {projects.map((project) => <option key={project._id} value={project._id}>{project.name}</option>)}
                </select>
                <button className="btn btn-primary btn-sm" type="submit">Link Project</button>
              </form>
              <div className="business-link-list">
                {(selectedBusiness.projects || []).map((project) => (
                  <div key={project._id} className="business-link-item">
                    <span>{project.name}</span>
                    <button className="btn btn-ghost btn-sm" onClick={() => handleUnlinkProject(project._id)}>Unlink</button>
                  </div>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}